/**
 * SwiperRegistry — three named maps for distinct lookup needs.
 *
 *   sections     Map<HTMLElement, Swiper[]>     A section's full swiper list,
 *                                               used to destroy on resize and
 *                                               to detect "already initialized".
 *
 *   syncGroups   Map<string, Swiper[]>          Swipers participating in a
 *                                               sync group keyed by data-sync-id.
 *
 *   byId         Map<string, Swiper>            One swiper per section.id, used
 *                                               by the back-compat shim and
 *                                               external callers.
 *
 * The previous single-map registry mixed all three into one Map, which was the
 * source of several timing bugs. Splitting them by intent eliminates the class
 * of "this Map sometimes holds an array, sometimes a Swiper" surprises.
 */

class SwiperRegistry {
  constructor() {
    this.sections = new Map();
    this.syncGroups = new Map();
    this.byId = new Map();
  }

  // ---------- section ↔ swipers[] ----------

  setSectionSwipers(section, swipers) {
    this.sections.set(section, swipers);
  }

  getSectionSwipers(section) {
    return this.sections.get(section);
  }

  hasSection(section) {
    return this.sections.has(section);
  }

  /**
   * Destroy and forget every swiper in the given section. Safe to call when
   * the section has no entry.
   */
  deleteSection(section) {
    const swipers = this.sections.get(section);
    if (swipers) {
      swipers.forEach((s) => {
        if (s && typeof s.destroy === 'function') {
          try {
            s.destroy(true, true);
          } catch (err) {
            console.warn('Failed to destroy swiper:', err);
          }
        }
      });
    }
    this.sections.delete(section);
  }

  forEachSection(cb) {
    this.sections.forEach(cb);
  }

  // ---------- sync groups ----------

  registerForSync(syncId, swiper) {
    if (!this.syncGroups.has(syncId)) {
      this.syncGroups.set(syncId, []);
    }
    this.syncGroups.get(syncId).push(swiper);
  }

  unregisterFromSync(syncId, swiper) {
    const group = this.syncGroups.get(syncId);
    if (!group) return;
    const idx = group.indexOf(swiper);
    if (idx !== -1) group.splice(idx, 1);
    if (group.length === 0) this.syncGroups.delete(syncId);
  }

  getSyncGroup(syncId) {
    return this.syncGroups.get(syncId) || [];
  }

  forEachSyncGroup(cb) {
    this.syncGroups.forEach(cb);
  }

  // ---------- by section.id ----------

  registerById(id, swiper) {
    if (id) this.byId.set(id, swiper);
  }

  getById(id) {
    return this.byId.get(id);
  }

  unregisterById(id) {
    this.byId.delete(id);
  }

  // ---------- whole-registry ops ----------

  clear() {
    this.sections.clear();
    this.syncGroups.clear();
    this.byId.clear();
  }
}

const registry = new SwiperRegistry();

export default registry;
export { SwiperRegistry };
